import { lerpHex } from '../math';
import { glowSpriteFor } from '../sprites';

/** The colour a hull fades toward at full night. */
const NIGHT_TINT = '12273a';

/** How far toward the night tint a hull goes at full darkness. */
const NIGHT_STRENGTH = 0.72;

/**
 * Every vessel drawing goes through this so hull colours fade toward a dark
 * silhouette as night falls.
 *
 * A lit white hull should not be visible at night, and fading the whole palette
 * also kills the pale bow and stern overhangs that would otherwise float in the
 * dark.
 *
 * Resolved colours are memoised for the current daylight level. The v89
 * prototype re-parsed the hex on every call, several hundred times a frame; the
 * output is identical either way.
 */
export class BoatPalette {
  private cache = new Map<string, string>();

  private cachedDaylight = Number.NaN;

  constructor(
    public readonly ctx: CanvasRenderingContext2D,
    private daylight = 1,
  ) {}

  setDaylight(daylight: number): void {
    if (daylight !== this.cachedDaylight) {
      this.cache.clear();
      this.cachedDaylight = daylight;
    }

    this.daylight = daylight;
  }

  /** Resolve an authored hull colour for the current light. */
  colour(hex: string): string {
    const cached = this.cache.get(hex);

    if (cached != null) {
      return cached;
    }

    const resolved = lerpHex(
      hex,
      NIGHT_TINT,
      (1 - this.daylight) * NIGHT_STRENGTH,
    );
    this.cache.set(hex, resolved);

    return resolved;
  }

  /**
   * A night-time nav light: a baked radial glow plus a small bright core. Call
   * this inside the boat's transformed space.
   */
  navGlow(
    x: number,
    y: number,
    radius: number,
    rgb: string,
    alpha: number,
  ): void {
    const { ctx } = this;
    const previousAlpha = ctx.globalAlpha;

    ctx.globalAlpha = previousAlpha * alpha;
    ctx.drawImage(
      glowSpriteFor(rgb),
      x - radius,
      y - radius,
      radius * 2,
      radius * 2,
    );
    ctx.globalAlpha = previousAlpha;

    ctx.fillStyle = `rgba(255,255,255,${Math.min(1, alpha * 1.2)})`;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.22, 0, Math.PI * 2);
    ctx.fill();
  }
}
